/**
 * useSignedThumbnailUrls – batch-generates signed URLs for thumbnail_path values.
 *
 * Returns a Map<thumbnailPath, signedUrl>.
 * Cached in React Query with a 50-minute staleTime (URLs expire in 60 min).
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const SIGNED_URL_TTL = 3600; // 1 hour
const STALE_TIME = 50 * 60 * 1000; // 50 minutes (refresh before expiry)

export function useSignedThumbnailUrls(paths: (string | null | undefined)[]) {
  const validPaths = paths.filter((p): p is string => !!p);
  // Sort for stable query key
  const sortedPaths = [...validPaths].sort();

  const { data: urlMap = new Map<string, string>(), isLoading } = useQuery({
    queryKey: ["signed-thumbnail-urls", sortedPaths],
    queryFn: async (): Promise<Map<string, string>> => {
      if (sortedPaths.length === 0) return new Map();

      const { data, error } = await supabase.storage
        .from("thumbnails")
        .createSignedUrls(sortedPaths, SIGNED_URL_TTL);

      if (error) {
        console.error("Failed to generate signed URLs:", error);
        return new Map();
      }

      const map = new Map<string, string>();
      data?.forEach((item) => {
        if (item.signedUrl && item.path) {
          map.set(item.path, item.signedUrl);
        }
      });
      return map;
    },
    enabled: sortedPaths.length > 0,
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 1.2,
  });

  return { urlMap, isLoading };
}

/**
 * Helper to resolve a thumbnail URL: prefer signed URL from path, fall back to legacy thumbnail_url.
 */
export function resolveThumbnailUrl(
  urlMap: Map<string, string>,
  thumbnailPath: string | null | undefined,
  thumbnailUrl: string | null | undefined
): string | null {
  if (thumbnailPath && urlMap.has(thumbnailPath)) {
    return urlMap.get(thumbnailPath)!;
  }
  return thumbnailUrl || null;
}
